/**
 * P-4 施策詳細 (`/board-initiatives/:id`)。設計: docs/design/v05-board-meeting-pdca-detail.md §7-4。
 */
import { useMemo, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { AlertTriangle } from 'lucide-react';
import { apiFetch, ApiClientError } from '@/lib/api';
import { toast } from '@/stores/toast';
import { useAuthStore } from '@/stores/auth';
import { Button } from '@/components/Button';
import { Modal, ConfirmDialog } from '@/components/Modal';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { BoardEmptyState } from '@/components/board/BoardEmptyState';
import { MonthPicker } from '@/components/MonthPicker';
import type { BoardNode } from '@/lib/board-node';

type BoardInitiativeOwner = { employee_id: string; name: string | null; role_label: string | null };
type WriteTier = 'manage' | 'node' | 'self' | null;
type BoardInitiative = {
  initiative_id: string; node_id: string; title: string; description: string | null;
  origin_meeting_id: string | null; origin_finding_id: string | null;
  owners: BoardInitiativeOwner[];
  due_date: string | null; status: 'draft' | 'active' | 'done' | 'cancelled' | 'carried_over';
  target_kpi_type: string | null; baseline_month: string | null; baseline_value: number | null;
  target_month: string | null; target_value: number | null; expected_impact_amount: number | null;
  created_at: string; updated_at: string; row_version: number;
  origin_finding: { finding_id: string; title: string; narrative: string; evidence: unknown } | null;
  // 2026-09-16 バグ報告対応: 「自分がこの施策に対してどのティアの書込み権を持つか」をAPIが
  // resolveWriteTier でそのまま返す。フロントエンドは認可ロジックを再実装せずこの値だけで
  // 編集可否を決める (以前は board_initiative:create のwritableNodeIdsを誤って流用しており、
  // self tierの担当者本人が一切編集できなくなっていた)。
  write_tier: WriteTier;
};
type ProgressRow = {
  progress_id: string; kind: 'manual_update' | 'auto_measurement'; recorded_month: string | null;
  measured_value: number | null; verdict: 'achieved' | 'partial' | 'missed' | 'not_measurable' | null;
  status_at_time: string | null; note: string | null; created_at: string;
};

const KPI_TYPE_LABEL: Record<string, string> = {
  cost_rate: '原価率', gross_margin: '粗利率', labor_rate: '人件費率', opex_rate: '諸経費率',
  sales: '売上', customer_count: '来客数', avg_price: '客単価',
};
const RATE_KPI_TYPES = new Set(['cost_rate', 'gross_margin', 'labor_rate', 'opex_rate']);
const VERDICT_LABEL: Record<string, string> = { achieved: '達成', partial: '一部達成', missed: '未達成', not_measurable: '測定不能' };
const VERDICT_CLASS: Record<string, string> = {
  achieved: 'bg-success-bg text-success-text border-success-border',
  partial: 'bg-surface-sunken text-text-sub border-surface-border',
  missed: 'bg-alert-bg text-alert-text border-alert-border',
  not_measurable: 'bg-surface-sunken text-text-sub border-surface-border',
};
const STATUS_LABEL: Record<BoardInitiative['status'], string> = {
  draft: '下書き', active: '進行中', done: '達成', cancelled: '取消', carried_over: '継続中',
};
const STATUS_VALUES = Object.keys(STATUS_LABEL) as BoardInitiative['status'][];

const formatKpiValue = (kpiType: string | null, value: number | null): string => {
  if (value == null) return '—';
  if (kpiType && RATE_KPI_TYPES.has(kpiType)) return `${(value * 100).toFixed(1)}%`;
  return value.toLocaleString('ja-JP');
};

export function BoardInitiativeDetailPage() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const qc = useQueryClient();
  const user = useAuthStore((s) => s.user);

  const [editOpen, setEditOpen] = useState(false);
  const [deleteConfirmOpen, setDeleteConfirmOpen] = useState(false);
  const [progressNote, setProgressNote] = useState('');

  const { data: initiative, isLoading, isError } = useQuery<BoardInitiative>({
    queryKey: ['board-initiative', id],
    queryFn: () => apiFetch(`/board-initiatives/${id}`),
    enabled: !!id,
  });

  const { data: progressData } = useQuery<{ progress: ProgressRow[] }>({
    queryKey: ['board-initiative-progress', id],
    queryFn: () => apiFetch(`/board-initiatives/${id}/progress`),
    enabled: !!id,
  });
  const progress = progressData?.progress ?? [];
  const latestAuto = progress.find((p) => p.kind === 'auto_measurement') ?? null;

  const { data: creatableData } = useQuery<{ nodes: BoardNode[] }>({
    queryKey: ['board-initiatives', 'creatable-nodes'],
    queryFn: () => apiFetch('/board-initiatives/creatable-nodes'),
  });
  const nodeById = useMemo(() => new Map((creatableData?.nodes ?? []).map((n) => [n.node_id, n])), [creatableData]);
  // §4-3-c: self tier は status/description のみ、node/manage tier はそれに加えて期待効果欄も編集可。
  const canWrite = !!initiative && initiative.write_tier !== null;
  const canWriteNodeFields = !!initiative && (initiative.write_tier === 'node' || initiative.write_tier === 'manage');
  const canManage = !!initiative && initiative.write_tier === 'manage';

  const measureMut = useMutation({
    mutationFn: () => apiFetch(`/board-initiatives/${id}/measure`, { method: 'POST' }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['board-initiative', id] });
      qc.invalidateQueries({ queryKey: ['board-initiative-progress', id] });
      toast.success('効果を測定しました');
    },
    onError: (e) => toast.error(e instanceof ApiClientError ? e.message : '効果測定に失敗しました'),
  });

  const noteMut = useMutation({
    mutationFn: () => apiFetch(`/board-initiatives/${id}/progress`, { method: 'POST', body: JSON.stringify({ note: progressNote }) }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['board-initiative-progress', id] });
      setProgressNote('');
      toast.success('進捗メモを記録しました');
    },
    onError: (e) => toast.error(e instanceof ApiClientError ? e.message : '進捗メモの記録に失敗しました'),
  });

  const editMut = useMutation({
    mutationFn: (patch: Record<string, unknown>) =>
      apiFetch(`/board-initiatives/${id}`, { method: 'PATCH', body: JSON.stringify({ ...patch, row_version: initiative!.row_version }) }),
    onSuccess: () => { qc.invalidateQueries({ queryKey: ['board-initiative', id] }); setEditOpen(false); toast.success('更新しました'); },
    onError: (e) => toast.error(e instanceof ApiClientError ? e.message : '更新に失敗しました'),
  });

  const statusMut = useMutation({
    mutationFn: (status: BoardInitiative['status']) =>
      apiFetch(`/board-initiatives/${id}`, { method: 'PATCH', body: JSON.stringify({ status, row_version: initiative!.row_version }) }),
    onSuccess: () => { qc.invalidateQueries({ queryKey: ['board-initiative', id] }); toast.success('ステータスを更新しました'); },
    onError: (e) => toast.error(e instanceof ApiClientError ? e.message : 'ステータスの更新に失敗しました'),
  });

  const deleteMut = useMutation({
    mutationFn: () => apiFetch(`/board-initiatives/${id}`, { method: 'DELETE' }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['board-initiatives'] });
      toast.success('施策を削除しました');
      navigate('/board-initiatives');
    },
    onError: (e) => toast.error(e instanceof ApiClientError ? e.message : '施策の削除に失敗しました'),
  });

  if (isLoading) return <p className="text-sm text-text-sub">読み込んでいます…</p>;
  if (isError || !initiative) return <p role="alert" className="text-sm text-alert-text">施策が見つからないか、閲覧する権限がありません。</p>;

  const measureDisabledReason = !initiative.target_kpi_type
    ? '効果を測定するには期待効果 (KPI・目標値) を登録してください'
    : user?.edition === 'lite'
      ? 'Lite版では効果測定は次期対応です'
      : null;

  return (
    <div className="space-y-4">
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div className="min-w-0">
          <div className="flex items-center gap-2 flex-wrap">
            <h1 className="text-page-title text-text-main break-words">{initiative.title}</h1>
            {canWrite ? (
              <Select value={initiative.status} onValueChange={(v) => statusMut.mutate(v as BoardInitiative['status'])}
                disabled={statusMut.isPending}>
                <SelectTrigger className="w-auto px-2 text-[11px]" aria-label="ステータスを変更"><SelectValue /></SelectTrigger>
                <SelectContent>
                  {STATUS_VALUES.map((s) => <SelectItem key={s} value={s}>{STATUS_LABEL[s]}</SelectItem>)}
                </SelectContent>
              </Select>
            ) : (
              <span className="text-[11px] px-1.5 py-0.5 rounded border whitespace-nowrap bg-surface-sunken text-text-sub border-surface-border">
                {STATUS_LABEL[initiative.status]}
              </span>
            )}
            {!canWrite && (
              <span className="text-[11px] px-1.5 py-0.5 rounded bg-alert-bg text-alert-text whitespace-nowrap">全社共通のため閲覧のみ</span>
            )}
          </div>
          <p className="text-sm text-text-sub mt-1">{nodeById.get(initiative.node_id)?.name ?? initiative.node_id}</p>
          {initiative.owners.length > 0 && (
            <div className="flex items-center gap-1.5 flex-wrap mt-1">
              <span className="text-sm text-text-sub">担当:</span>
              {initiative.owners.map((o) => (
                <span key={o.employee_id}
                  className="text-[11px] px-1.5 py-0.5 rounded border border-surface-border bg-white text-text-body whitespace-nowrap">
                  {o.name ?? '(不明)'}{o.role_label ? `（${o.role_label}）` : ''}
                </span>
              ))}
            </div>
          )}
          {initiative.due_date && <p className="text-sm text-text-sub tabular-nums">期限: {initiative.due_date}</p>}
          {initiative.description && <p className="text-sm text-text-body mt-2 whitespace-pre-wrap break-words">{initiative.description}</p>}
        </div>
        <div className="flex items-center gap-2 shrink-0">
          {canWriteNodeFields && <Button variant="secondary" size="sm" onClick={() => setEditOpen(true)}>期待効果を編集</Button>}
          {canManage && (
            <Button variant="danger-outline" size="sm" aria-label="施策を削除" onClick={() => setDeleteConfirmOpen(true)}>削除</Button>
          )}
        </div>
      </div>

      {/* 期待 vs 実績 (AC-6) */}
      <section className="border border-surface-border rounded-lg bg-white p-4 space-y-3">
        <h2 className="text-sm font-semibold text-text-body">期待 vs 実績</h2>
        {!initiative.target_kpi_type ? (
          <BoardEmptyState title="期待効果が未設定です" description="効果を測定するには期待効果を登録してください。" />
        ) : (
          <>
            {/* 375px未満は縦3行 (§7-4末尾)。table 要素を使わず flex で組んでおけば自然に縮退する */}
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-3 text-sm">
              <div>
                <p className="text-xs text-text-sub">基準 ({initiative.baseline_month?.slice(0, 7) ?? '未設定'})</p>
                <p className="text-text-body tabular-nums font-semibold">{formatKpiValue(initiative.target_kpi_type, initiative.baseline_value)}</p>
              </div>
              <div>
                <p className="text-xs text-text-sub">目標 ({initiative.target_month?.slice(0, 7) ?? '未設定'})</p>
                <p className="text-text-body tabular-nums font-semibold">{formatKpiValue(initiative.target_kpi_type, initiative.target_value)}</p>
              </div>
              <div>
                <p className="text-xs text-text-sub">実測 {latestAuto?.recorded_month ? `(${latestAuto.recorded_month.slice(0, 7)})` : ''}</p>
                <p className="text-text-body tabular-nums font-semibold">{formatKpiValue(initiative.target_kpi_type, latestAuto?.measured_value ?? null)}</p>
              </div>
            </div>
            {latestAuto?.verdict && (
              <span className={`inline-block text-[11px] px-1.5 py-0.5 rounded border whitespace-nowrap ${VERDICT_CLASS[latestAuto.verdict]}`}>
                {VERDICT_LABEL[latestAuto.verdict]}
              </span>
            )}
            <p className="text-xs text-text-sub">対象KPI: {KPI_TYPE_LABEL[initiative.target_kpi_type] ?? initiative.target_kpi_type} / 期待効果: {initiative.expected_impact_amount != null ? `${initiative.expected_impact_amount.toLocaleString('ja-JP')}円` : '—'}</p>
            {measureDisabledReason ? (
              <Button variant="secondary" size="sm" disabled aria-describedby="measure-reason">効果を測定</Button>
            ) : (
              <Button variant="secondary" size="sm" loading={measureMut.isPending} onClick={() => measureMut.mutate()}>効果を測定</Button>
            )}
            {measureDisabledReason && <p id="measure-reason" className="text-xs text-text-sub">{measureDisabledReason}</p>}
          </>
        )}
      </section>

      {/* 進捗履歴 */}
      <section className="space-y-2">
        <h2 className="text-sm font-semibold text-text-body">進捗履歴</h2>
        {canWrite && (
          <div className="flex flex-wrap gap-2">
            <input type="text" value={progressNote} maxLength={2000} placeholder="進捗メモを記録"
              onChange={(e) => setProgressNote(e.target.value)}
              className="flex-1 min-w-0 min-h-[44px] px-3 rounded border border-surface-border text-sm" />
            <Button variant="secondary" size="sm" disabled={progressNote.trim() === ''} loading={noteMut.isPending}
              onClick={() => noteMut.mutate()}>記録</Button>
          </div>
        )}
        {progress.length === 0 ? (
          <BoardEmptyState title="まだ進捗が記録されていません" />
        ) : (
          <ul className="space-y-2">
            {progress.map((p) => (
              <li key={p.progress_id} className="border border-surface-border rounded-lg bg-white p-3">
                <div className="flex items-center justify-between gap-2 flex-wrap">
                  <span className="text-xs text-text-sub tabular-nums">{p.created_at.slice(0, 16).replace('T', ' ')}</span>
                  {p.kind === 'auto_measurement' && p.verdict && (
                    <span className={`text-[11px] px-1.5 py-0.5 rounded border whitespace-nowrap ${VERDICT_CLASS[p.verdict]}`}>{VERDICT_LABEL[p.verdict]} (自動測定)</span>
                  )}
                </div>
                {p.note && <p className="text-sm text-text-body mt-1 whitespace-pre-wrap break-words">{p.note}</p>}
              </li>
            ))}
          </ul>
        )}
      </section>

      {/* 由来 */}
      <section className="space-y-2">
        <h2 className="text-sm font-semibold text-text-body">由来</h2>
        {initiative.origin_meeting_id && (
          <button type="button" onClick={() => navigate(`/board-meetings/${initiative.origin_meeting_id}`)}
            className="text-sm text-group-board underline underline-offset-2 min-h-[44px]">
            起票元の会議を開く
          </button>
        )}
        {initiative.origin_finding_id ? (
          initiative.origin_finding ? (
            <div className="border border-surface-border rounded-lg bg-white p-3">
              <p className="text-sm font-semibold text-text-body break-words">{initiative.origin_finding.title}</p>
              <p className="text-sm text-text-sub mt-1 whitespace-pre-wrap break-words">{initiative.origin_finding.narrative}</p>
            </div>
          ) : (
            <p className="text-xs text-text-sub">この論点の詳細は経営会議の閲覧権限が必要です。</p>
          )
        ) : !initiative.origin_meeting_id ? (
          <p className="text-xs text-text-sub">会議外で登録された施策です。</p>
        ) : null}
      </section>

      {canWriteNodeFields && (
        <EditInitiativeModal open={editOpen} initiative={initiative} onClose={() => setEditOpen(false)}
          onSave={(patch) => editMut.mutate(patch)} saving={editMut.isPending} />
      )}
      {canManage && (
        <ConfirmDialog open={deleteConfirmOpen} title="施策を削除しますか？"
          message={`「${initiative.title}」と、その進捗履歴・担当者の割当を完全に削除します。この操作は取り消せません。起票元の論点がある場合は再度施策化できる状態に戻ります。`}
          confirmLabel="削除する" destructive
          loading={deleteMut.isPending}
          onConfirm={() => deleteMut.mutate()}
          onClose={() => setDeleteConfirmOpen(false)} />
      )}
    </div>
  );
}

type EditDraft = {
  targetKpiType: string; baselineMonth: string; baselineValue: string;
  targetMonth: string; targetValue: string; expectedImpactAmount: string;
};

function EditInitiativeModal({ open, initiative, onClose, onSave, saving }: {
  open: boolean; initiative: BoardInitiative; onClose: () => void;
  onSave: (patch: Record<string, unknown>) => void; saving: boolean;
}) {
  const [draft, setDraft] = useState<EditDraft>(() => ({
    targetKpiType: initiative.target_kpi_type ?? '',
    baselineMonth: initiative.baseline_month?.slice(0, 7) ?? '',
    baselineValue: initiative.baseline_value != null ? String(initiative.baseline_value) : '',
    targetMonth: initiative.target_month?.slice(0, 7) ?? '',
    targetValue: initiative.target_value != null ? String(initiative.target_value) : '',
    expectedImpactAmount: initiative.expected_impact_amount != null ? String(initiative.expected_impact_amount) : '',
  }));
  const isRate = draft.targetKpiType ? RATE_KPI_TYPES.has(draft.targetKpiType) : false;

  return (
    <Modal open={open} title="期待効果を編集" onClose={onClose} size="md"
      footer={(
        <>
          <Button variant="secondary" onClick={onClose}>キャンセル</Button>
          <Button variant="primary" loading={saving} onClick={() => onSave({
            target_kpi_type: draft.targetKpiType || null,
            baseline_month: draft.baselineMonth ? `${draft.baselineMonth}-01` : null,
            baseline_value: draft.baselineValue ? (isRate ? Number(draft.baselineValue) / 100 : Number(draft.baselineValue)) : null,
            target_month: draft.targetMonth ? `${draft.targetMonth}-01` : null,
            target_value: draft.targetValue ? (isRate ? Number(draft.targetValue) / 100 : Number(draft.targetValue)) : null,
            expected_impact_amount: draft.expectedImpactAmount ? Number(draft.expectedImpactAmount) : null,
          })}>保存</Button>
        </>
      )}>
      <div className="space-y-3">
        <div className="flex items-start gap-2 text-xs text-text-sub bg-surface-sunken rounded p-2">
          <AlertTriangle size={14} className="shrink-0 mt-0.5" aria-hidden />
          保存すると効果測定が可能になります。
        </div>
        <div>
          <label className="block text-xs text-text-sub mb-1" htmlFor="ei-kpi">対象KPI</label>
          <Select value={draft.targetKpiType || '__none__'} onValueChange={(v) => setDraft({ ...draft, targetKpiType: v === '__none__' ? '' : v })}>
            <SelectTrigger id="ei-kpi" aria-label="対象KPI"><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value="__none__">未設定</SelectItem>
              {Object.entries(KPI_TYPE_LABEL).map(([k, v]) => <SelectItem key={k} value={k}>{v}</SelectItem>)}
            </SelectContent>
          </Select>
        </div>
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
          <div>
            <label className="block text-xs text-text-sub mb-1" htmlFor="ei-bm">基準月・値{isRate ? ' (%)' : ''}</label>
            <div className="flex gap-1.5">
              <MonthPicker id="ei-bm" aria-label="基準月" value={draft.baselineMonth} onChange={(v) => setDraft({ ...draft, baselineMonth: v })}
                className="min-h-[44px] px-2 rounded border border-surface-border text-sm flex-1 min-w-0" />
              <input type="number" step="any" aria-label="基準値" value={draft.baselineValue}
                onChange={(e) => setDraft({ ...draft, baselineValue: e.target.value })}
                className="min-h-[44px] px-2 rounded border border-surface-border text-sm w-24 appearance-none [&::-webkit-inner-spin-button]:appearance-none [&::-webkit-outer-spin-button]:appearance-none" />
            </div>
          </div>
          <div>
            <label className="block text-xs text-text-sub mb-1" htmlFor="ei-tm">目標月・値{isRate ? ' (%)' : ''}</label>
            <div className="flex gap-1.5">
              <MonthPicker id="ei-tm" aria-label="目標月" value={draft.targetMonth} onChange={(v) => setDraft({ ...draft, targetMonth: v })}
                className="min-h-[44px] px-2 rounded border border-surface-border text-sm flex-1 min-w-0" />
              <input type="number" step="any" aria-label="目標値" value={draft.targetValue}
                onChange={(e) => setDraft({ ...draft, targetValue: e.target.value })}
                className="min-h-[44px] px-2 rounded border border-surface-border text-sm w-24 appearance-none [&::-webkit-inner-spin-button]:appearance-none [&::-webkit-outer-spin-button]:appearance-none" />
            </div>
          </div>
        </div>
        <div>
          <label className="block text-xs text-text-sub mb-1" htmlFor="ei-impact">期待効果 (円)</label>
          <input id="ei-impact" type="number" step="1" value={draft.expectedImpactAmount}
            onChange={(e) => setDraft({ ...draft, expectedImpactAmount: e.target.value })}
            className="w-full min-h-[44px] px-3 rounded border border-surface-border text-sm tabular-nums appearance-none [&::-webkit-inner-spin-button]:appearance-none [&::-webkit-outer-spin-button]:appearance-none" />
        </div>
      </div>
    </Modal>
  );
}
